import PropTypes from 'prop-types';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import {roundTo} from 'round-to';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DownloadInvoiceButton from './DownloadInvoiceButton';
import DownloadNoteButton from './DownloadNoteButton';
import EditNoteButton from './EditNoteButton';
import EditSessionButton from './EditSessionButton';
import SessionStatus from './SessionStatus';
import SessionBookmark from './SessionBookmark';
import PatientSummary from '../patients/PatientSummary';
import CancelSessionDialog from './CancelSessionDialog';
import SessionActions from './SessionActions';
import BackButton from "../common/BackButton";
import CustomBox from "../common/CustomBox";

import {
  calculateSessionTotal,
  calculateSessionRate,
  formatSessionDate,
  formatSessionTime,
  formatSessionLocation,
  formatSessionVirtualLink,
  getSessionCategory,
} from '../../helpers/sessions';
import { formatCaseName } from '../../helpers/cases';

const DisplayField = props => {
  if (props.hide) return null;
  return (
    <Stack direction='row' spacing={2} alignItems='center' marginBottom={2}>
      <Typography
        variant='large600Tight'
        style={{minWidth: '100px'}}
        color='text.light'
      >{props.label}</Typography>
      {props.value
        ? <Typography variant='large'>
          {props.value}
        </Typography>
        : null
      }
      {props.children}
    </Stack>
  );
};

const getFields = ({session, isPatient}) => ([
  {
    label: 'Status',
    value: () => null,
    render: session => <SessionStatus session={session}/>,
    section: 'overview',
    hidden: () => false,
  },
  {
    label: 'Category',
    value: session => getSessionCategory(session),
    section: 'overview',
    hidden: () => false,
  },
  {
    label: 'Session Type',
    value: session => session?.appointment_scheduled?.session_type?.title,
    section: 'overview',
    hidden: session => !session?.appointment_scheduled?.session_type?.title,
  },
  {
    label: 'Case',
    value: session => formatCaseName(session?.case),
    section: 'overview',
    hidden: session => !formatCaseName(session?.case) && !isPatient,
  },
  {
    label: 'Instructions',
    value: session => (
      <span style={{whiteSpace: 'pre-line'}}>
        {session?.instructions || session?.appointment_scheduled?.session_type?.instructions}
      </span>
    ),
    section: 'overview',
    hidden: session => !session?.instructions && !session?.appointment_scheduled?.session_type?.instructions,
  },
  {
    label: 'Cancellation Note',
    value: session => session.cancel_note,
    section: 'overview',
    hidden: session => isPatient || !session.cancel_note,
  },
  {
    label: 'No Show?',
    value: session => session.is_no_show ? 'Yes' : 'No',
    section: 'overview',
    hidden: session => isPatient || !session.is_canceled,
  },
  {
    label: 'Date',
    value: session => formatSessionDate(session),
    section: 'scheduling',
    hidden: () => false,
  },
  {
    label: 'Time',
    value: session => formatSessionTime(session),
    section: 'scheduling',
    hidden: () => false,
  },
  {
    label: 'Duration',
    value: session => `${get(session, 'duration') || ''}${get(session, 'duration') ? ' minutes' : ''}`,
    section: 'scheduling',
    hidden: () => false,
  },
  {
    label: 'Rate',
    value: session => `$${get(session, 'rate') || calculateSessionRate(session)}`,
    section: 'billing',
    hidden: () => false,
  },
  {
    label: 'Billing Type',
    value: session => session?.case?.billing_category || '',
    section: 'billing',
    hidden: () => false,
  },
  {
    label: 'Payer',
    value: session => session?.case?.payer_plan?.payer?.name || '',
    section: 'billing',
    hidden: () => false,
  },
  {
    label: 'Discount',
    value: session => `${session?.discount || '0'}%`,
    section: 'billing',
    hidden: session => !session?.discount,
  },
  {
    label: 'Total',
    value: session => (
      session.is_completed
      ? `$${get(session, 'total') || ''}`
      : `$${calculateSessionTotal(session)}`
    ),
    section: 'billing',
    hidden: session => session.is_completed && !get(session, 'total'),
  },
  {
    label: 'Location',
    value: session => formatSessionLocation(session, {returnLink: true}),
    section: 'location',
    hidden: () => false,
  },
  {
    label: 'Meeting Link',
    value: session => formatSessionVirtualLink(session, {returnLink: true}),
    section: 'location',
    hidden: session => session.location !== 'VIRTUAL',
  }
]);

const getSectionLabel = sectionName => {
  if (sectionName === 'overview') return 'Overview';
  if (sectionName === 'scheduling') return 'Scheduling';
  if (sectionName === 'billing') return 'Estimated Billing';
  if (sectionName === 'location') return 'Location';
};

const ViewSessionScreen = props => {
  const {session: s, isPatient} = props;
  const appointment = get(props, 'appointment') || {};
  const fields = getFields({session: {...s, appointment}, isPatient});
  const fieldsBySection = groupBy(fields, f => f.section);
  const sections = Object.keys(fieldsBySection);
  const defaultButton = {
    size: 'medium',
    variant: 'primaryOutline',
    textVariant: 'large600TightInter',
    // textProps: {
    //   variant: 'large600TightInter',
    //   noWrap: true,
    // }
  };
  const isProvider = isPatient === false;
  return (
    <Stack spacing={2} padding={1}>
      <Box>
        <BackButton />
      </Box>
      {
        isPatient
          ? null
          : <PatientSummary patient={s.patient} showCases={isProvider}/>
      }
      <Stack direction={{xs: 'column', xsm: 'row'}} spacing={1}>
        {
          isPatient && !s.is_completed
            ? <SessionActions isPatient={isPatient} session={s} {...defaultButton} />
            : null
        }
        {
          isPatient || s.is_canceled
            ? null
            : <EditNoteButton 
                session={s} 
                afterClose={props?.onForceRefreshPDFs} 
                {...defaultButton}
              />
        }
        {
          s.is_completed || s.is_canceled
            ? null
            : <EditSessionButton
              session={s}
              onSubmitted={props.refetch}
              {...defaultButton}
            />
        }
        {s.is_completed || s.is_canceled || isPatient
          ? null
          : <CancelSessionDialog session={s} isPatient={isPatient} {...defaultButton}/>
        }
        {
          s.is_completed
            ? <DownloadInvoiceButton 
                session={s} 
                pdfMustRefresh={props.refreshInvoicePDF}
                afterDownload={props.onRefreshInvoicePDF}
                {...defaultButton}
              />
            : null
        }
        {
          s.is_completed
            ? <DownloadNoteButton 
                session={s} {...defaultButton}
                pdfMustRefresh={props.refreshNotePDF}
                afterDownload={props.onRefreshNotePDF}
              />
            : null
        }
      </Stack>
      <Grid
        container
        spacing={2}
        padding={0}
        margin={0}
        justifyContent='space-between'
        style={{marginLeft: '-16px'}}
      >
        {sections.map(section =>
          <Grid item xs={12} sm={12} md={6} padding={0} margin={0}>
            <CustomBox className='h-100'>
              <Typography variant='h2' padding={2}>
                {getSectionLabel(section)}
              </Typography>
              <Divider/>
              <Stack padding={2}>
                {(fieldsBySection[section] || []).map(f =>
                  <DisplayField
                    label={f.label}
                    value={f.value(s)}
                    hide={f.hidden(s)}
                  >
                    {f.render ? f.render(s) : null}
                  </DisplayField>
                )}
              </Stack>
            </CustomBox>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

ViewSessionScreen.defaultProps = {
  refetch: () => (true),
};

ViewSessionScreen.propTypes = {
  session: PropTypes.object,
};

export default ViewSessionScreen;
